import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-799" />;
export function Decode() {
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Decoder',
    description: 'Decoder takes your inputs and returns a structural map of how you operate. A diagnostic system for human patterns.',
    brand: {
      '@type': 'Brand',
      name: 'Lazy Monk Labs'
    }
  };
  return <>
      <Helmet data-id="element-800">
        <title data-id="element-801">Decoder — Structural Diagnostic System | Lazy Monk Labs</title>
        <meta name="description" content="Decoder takes your inputs and returns a structural map of how you operate. Not therapy. Not coaching. A diagnostic system." data-id="element-802" />
        <meta name="robots" content="index, follow" data-id="element-803" />
        <link rel="canonical" href="https://lazymonklabs.com/decoder" data-id="element-804" />
        <script type="application/ld+json" data-id="element-805">
          {JSON.stringify(productStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-806">
        {/* Navigation */}
        <header data-id="element-807">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-808">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-809">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-810">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-811" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-812">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-813">
                <Link to="/decoder" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-mono" data-id="element-814">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-815">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-816">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-817">
          {/* 1. WHAT IT DOES */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-818">
            <CoordinateGrid data-id="element-819" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-820">
              <div className="space-y-6" data-id="element-821">
                <h1 className="text-5xl md:text-7xl font-serif leading-none text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-822">
                  Decoder maps how you operate.
                </h1>

                <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-3xl" data-id="element-823">
                  It takes a small set of inputs and returns a structural read
                  of your default patterns — how you decide, where you get
                  stuck, and what keeps repeating.
                </p>
              </div>

              <div className="pt-8" data-id="element-824">
                <a href="https://app.lazymonklabs.com" className="inline-block px-10 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm uppercase tracking-widest font-medium hover:bg-gray-200 transition-colors rounded-sm" data-id="element-825">
                  Try a Decode
                </a>
              </div>
            </div>
          </section>

          {/* 2. WHAT IT'S NOT */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-826">
            <div className="max-w-4xl mx-auto" data-id="element-827">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-828">
                What this is not
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-829">
                <div className="space-y-6 text-base text-gray-400" data-id="element-830">
                  <p data-id="element-831">Not therapy. It doesn't work with emotions or stories.</p>
                  <p data-id="element-832">Not coaching. It doesn't guide, motivate, or suggest.</p>
                  <p data-id="element-833">Not a personality test. It doesn't score or label you.</p>
                </div>

                <div className="space-y-6" data-id="element-834">
                  <p className="text-base text-[#F5F5F5]" data-id="element-835">
                    Decoder is a diagnostic system. It detects structural
                    patterns in how you operate — and shows them to you clearly.
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-836">
                    Think of it as a structural scan, not an assessment.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 3. HOW IT WORKS */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-837">
            <div className="max-w-4xl mx-auto" data-id="element-838">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-839">
                How it works
              </h2>

              <div className="space-y-12" data-id="element-840">
                {/* Step 1 */}
                <div className="space-y-3" data-id="element-841">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-842">
                    01 — You provide inputs
                  </p>
                  <p className="text-base text-gray-400" data-id="element-843">
                    Birth details (as structural anchors), plus optional
                    behavioral questions or situation context.
                  </p>
                </div>

                <div className="flex justify-center" data-id="element-844">
                  <div className="text-gray-600 text-2xl" data-id="element-845">↓</div>
                </div>

                {/* Step 2 */}
                <div className="space-y-3" data-id="element-846">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-847">
                    02 — Decoder builds a pattern signature
                  </p>
                  <p className="text-base text-gray-400" data-id="element-848">
                    An internal structural model of how you tend to operate
                    under pressure, uncertainty, and responsibility.
                  </p>
                </div>

                <div className="flex justify-center" data-id="element-849">
                  <div className="text-gray-600 text-2xl" data-id="element-850">↓</div>
                </div>

                {/* Step 3 */}
                <div className="space-y-3" data-id="element-851">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-852">
                    03 — You receive a structured read
                  </p>
                  <p className="text-base text-gray-400" data-id="element-853">
                    A clear, readable map of your patterns — through the lens
                    you chose (self, work, relationships, or time).
                  </p>
                </div>

                <div className="pt-8 border-t border-[#2B2B2B]" data-id="element-854">
                  <p className="text-base text-[#F5F5F5]" data-id="element-855">
                    Same engine. Different lenses. Each Decode shows a different
                    angle of the same underlying structure.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 4. WHAT YOU RECEIVE */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-856">
            <div className="max-w-4xl mx-auto" data-id="element-857">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-858">
                What you receive
              </h2>

              <div className="space-y-8" data-id="element-859">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-860">
                  <div className="border border-[#2B2B2B] p-8 space-y-4" data-id="element-861">
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-862">
                      Your default operating pattern
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-863">
                      How you naturally process decisions, handle pressure, and
                      respond to uncertainty.
                    </p>
                  </div>

                  <div className="border border-[#2B2B2B] p-8 space-y-4" data-id="element-864">
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-865">
                      Your recurring loops
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-866">
                      The patterns that keep showing up — in work, in
                      relationships, in transitions.
                    </p>
                  </div>

                  <div className="border border-[#2B2B2B] p-8 space-y-4" data-id="element-867">
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-868">
                      Your structural constraints
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-869">
                      Where you tend to get stuck — not because of effort, but
                      because of how the system is wired.
                    </p>
                  </div>

                  <div className="border border-[#2B2B2B] p-8 space-y-4" data-id="element-870">
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-871">
                      Your leverage points
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-872">
                      Where small shifts in awareness create the most structural
                      change.
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 pt-4" data-id="element-873">
                  You don't get advice. You get a map. What you do with it is
                  yours.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-874">
            <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-875">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-876">
                See your pattern.
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-877">
                <a href="https://app.lazymonklabs.com" className="px-12 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors" data-id="element-878">
                  Start a Decode
                </a>
              </div>

              <div data-id="element-879">
                <Link to="/" className="inline-flex items-center gap-3 text-sm text-gray-600 hover:text-gray-400 transition-colors" data-id="element-880">
                  <span data-id="element-881">Back to Home</span>
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-882">
          <div className="max-w-6xl mx-auto" data-id="element-883">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-884">
              <div data-id="element-885">
                <p className="text-sm text-gray-500 font-mono" data-id="element-886">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-887">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-888">
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-889">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-890">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-891">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-892">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}